import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchContractStatuses } from '../data-provider';
import type { ContractStatus, ContractStatuses } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

export const contractStatusesQueryOptions = (
  { companyId }: { companyId: string },
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const contractStatuses = await fetchContractStatuses({ signal });
      put(contractStatuses.list, { companyId });
      return contractStatuses;
    },
    queryKey: CACHE_KEYS.contractStatuses({ companyId }),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_QUERY_CONTRACT_STATUSES_KEY = (companyId: string) =>
  contractStatusesQueryOptions({ companyId }).queryKey;

const getContractStatusFallback = (contractStatusId: string): ContractStatus => {
  logger.warn(`Contract status "${contractStatusId}" was not found. Verify the data model.`);
  return {
    id: contractStatusId,
    label: contractStatusId,
  };
};

export const put = async (contractStatuses: ContractStatus[], { companyId }: { companyId: string }) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CONTRACT_STATUSES_KEY(companyId));

  const newContractStatuses = contractStatuses.reduce(
    (acc, contractStatus) => {
      acc[String(contractStatus.id)] = contractStatus;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_CONTRACT_STATUSES_KEY(companyId), {
    list: Object.values(newContractStatuses),
    map: newContractStatuses,
  });
};

export const getAll = ({ companyId }: { companyId: string }): ContractStatuses | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_CONTRACT_STATUSES_KEY(companyId));
};

export const find = async (contractStatusId: string, { companyId }: { companyId: string }): Promise<ContractStatus> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CONTRACT_STATUSES_KEY(companyId));

  const contractStatus = data?.map[contractStatusId];
  if (contractStatus) {
    return contractStatus;
  }

  try {
    const newData = await queryClient.query(contractStatusesQueryOptions({ companyId }));
    if (newData.map[contractStatusId]) {
      void put(newData.list, { companyId });
      return newData.map[contractStatusId];
    }
  } catch {
    return getContractStatusFallback(contractStatusId);
  }

  return getContractStatusFallback(contractStatusId);
};

export const findAll = async (
  contractStatusIds: string[],
  { companyId }: { companyId: string },
): Promise<ContractStatus[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CONTRACT_STATUSES_KEY(companyId));

  const missingIds: string[] = [];
  const contractStatuses: ContractStatus[] = [];

  for (const contractStatusId of contractStatusIds) {
    const status = data?.map[contractStatusId];
    if (status) {
      contractStatuses.push(status);
    } else {
      missingIds.push(contractStatusId);
    }
  }

  if (!missingIds.length) {
    return contractStatuses;
  }

  try {
    const newData = await queryClient.query(contractStatusesQueryOptions({ companyId }));
    void put(newData.list, { companyId });
    for (const missingId of missingIds) {
      const status = newData.map[missingId];
      if (status) {
        contractStatuses.push(status);
      } else {
        contractStatuses.push(getContractStatusFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      contractStatuses.push(getContractStatusFallback(missingId));
    }
  }

  return contractStatuses;
};
